// Copyright (c) Docugami, Inc. All rights reserved.

import { withAuthenticationRequired } from '@auth0/auth0-react';
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { PageLoadingFallback } from 'components/common/PageLoadingFallback';
import { Config } from 'config';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAuthenticateRequests } from 'hooks/AuthenticateRequests';
import { useDgAuth } from 'hooks/DgAuthentication';
import { useFileManagerReduxInterop } from 'hooks/FileManagerReduxInterop';
import { DEVELOPER_HUB_PAGE, NavigationType, WORK_PATH_PAGE, useNavigationService } from 'hooks/navigationService';
import { useNetworkErrorTracker } from 'hooks/networkErrorTracker';
import { useOrgStatus } from 'hooks/OrgStatus';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useTelemetryContext, useTelemetryUserContext } from 'hooks/TelemetryContext';
import { useUploadProgress } from 'hooks/UploadProgress';
import { stopSignalRConnection } from 'networking/OrganizationSignalRConnection';
import { NotFoundPage } from 'pages/NotFoundPage';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';
import { StorageManagerInstance } from 'storage/StorageManager';
import { selectSubscriptionFriendlyName } from 'store/AccountInfoHelpers';
import { connectToOrganizationSignalR, fetchOrganizationAccount } from 'store/organization/actions';
import { useGetIsFirstArrivalQuery } from 'store/organization/organizationSlice';
import { useGetWorkPathsQuery } from 'store/work_path/workPathSlice';
import { ChameleonTelemetryProvider } from 'telemetry/ChameleonTelemetryProvider';
import { TelemetryContext } from 'telemetry/TelemetryContext';
import { TelemetryEvent, UserbackClosedSchema, UserbackOpenedSchema, UserbackSendingSchema, UserbackSentSchema } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { ITelemetryProvider } from 'telemetry/TelemetryProvider';
import { registerDocugamiIcons } from '../../../hooks/dgIcons';
import { AppState } from '../../../store';
import { reportErrorEncountered } from '../../../store/authentication/actions';
import { processUrlHashParameters } from '../../../store/urlhashparameters/actions';
import withOrgAdminRoleRequired from '../utils/AdminSecuredComponent';
import { ConfirmationDialog } from '../utils/ConfirmationDialog';
import { KeyboardAccelerators } from '../utils/KeyboardAccelerators';
import withSupportedPlatformRequired from '../utils/UnsupportedPlatform';
import withOrganizationRequired from '../utils/WithOrganizationRequired';
import { AppShell } from './AppShell';
import CreateOrganization from './CreateOrganization';
import { useGetRootDirectoryIdQuery } from 'store/frontend/documentsEndpoints';

// Lazy-loaded page components for code splitting
const AccountPage = lazy(() => import('pages/AccountPage').then(m => ({ default: m.AccountPage })));
const BillingPage = lazy(() => import('pages/BillingPage').then(m => ({ default: m.BillingPage })));
const DashboardPage = lazy(() => import('pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const DeveloperHubPage = lazy(() => import('pages/DeveloperHubPage').then(m => ({ default: m.DeveloperHubPage })));
const ProjectPage = lazy(() => import('pages/ProjectPage').then(m => ({ default: m.ProjectPage })));
const WorkPathRouting = lazy(() => import('pages/WorkPathRouting').then(m => ({ default: m.WorkPathRouting })));

// Lazy-loaded modal components - these are rarely used on initial load
const GlobalWorkPathModal = lazy(() => import('components/work_path/WorkPathCreationModal').then(m => ({ default: m.GlobalWorkPathModal })));
const GlobalAddDocsModal = lazy(() => import('components/myContentExplorer/AddDocsView').then(m => ({ default: m.GlobalAddDocsModal })));
const UpgradeAccountModal = lazy(() => import('components/common/LimitsContainer').then(m => ({ default: m.UpgradeAccountModal })));

const chmln = require('@chamaeleonidae/chmln');
const { v4: uuidv4 } = require('uuid');

dayjs.extend(relativeTime);
initializeIcons();
registerDocugamiIcons();


const anonymousUrls = [`${Config.apiUrl}/ClientConfiguration`, `https://${Config.auth0.domain}/dbconnections/change_password`];

const loginOptions = { loginOptions: { appState: { hash: window.location.hash } } };
const SecuredWorkPathRouting = withSupportedPlatformRequired(withAuthenticationRequired(withOrganizationRequired(WorkPathRouting), loginOptions));
const SecuredDashboardPage = withAuthenticationRequired(withOrganizationRequired(DashboardPage), loginOptions);
const SecuredProjectPage = withAuthenticationRequired(withOrganizationRequired(ProjectPage), loginOptions);
const SecuredAccountPage = withAuthenticationRequired(withOrganizationRequired(AccountPage), loginOptions);
const SecuredBillingPage = withAuthenticationRequired(withOrganizationRequired(withOrgAdminRoleRequired(BillingPage, { redirectIfNotAdmin: true })), loginOptions);
const SecuredOrganization = withAuthenticationRequired(CreateOrganization, loginOptions);
const SecuredDeveloperHubPage = withSupportedPlatformRequired(withAuthenticationRequired(withOrganizationRequired(withOrgAdminRoleRequired(DeveloperHubPage, { redirectIfNotAdmin: true })), loginOptions));

export const App = (): JSX.Element => {
    const { error, isLoading, isAuthenticated, user, hasOrgClaims } = useDgAuth();
    useAuthenticateRequests(anonymousUrls);
    useNetworkErrorTracker();

    const errorEncountered = useSelector((state: AppState) => state.authentication.errorEncountered);
    const [prevPath, setPrevPath] = useState('');
    const loginUrl = useSelector((state: AppState) => state.authentication.loginUrl);
    const curOrg = useSelector((state: AppState) => state.organizations.currentOrganization);
    const userEmail = user?.email;

    const navService = useNavigationService();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { isUploading } = useUploadProgress();
    const { needToCreateAnOrg, currentOrgInitializing } = useOrgStatus();

    useGetWorkPathsQuery(!!curOrg?.id && isAuthenticated ? undefined : skipToken);
    useGetRootDirectoryIdQuery(!!curOrg?.id && isAuthenticated ? undefined : skipToken);

    const subscriptionTypeFriendlyName = useAppSelector(selectSubscriptionFriendlyName)
    const context = useMemo(() => ({
        subscriptionType: subscriptionTypeFriendlyName
    }), [subscriptionTypeFriendlyName]);

    useTelemetryContext(context);
    useTelemetryUserContext();
    useFileManagerReduxInterop();

    const { data: showFirstArrival } = useGetIsFirstArrivalQuery(!!curOrg?.id && isAuthenticated ? undefined : skipToken);

    useEffect(() => {
        if (showFirstArrival && location.pathname.match(new RegExp(WORK_PATH_PAGE, 'g'))) {
            navService.navToHomePage(NavigationType.PushNewPage);
        }
    }, [location.pathname, navService, showFirstArrival])

    useEffect(() => {
        if (window.location.pathname !== '/callback') {
            dispatch(processUrlHashParameters(window.location.hash, window.location.search));
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [])

    useEffect(() => {
        if (!!error) {
            dispatch(reportErrorEncountered('Auth Error', error));
        }
    }, [error, dispatch])

    useEffect(() => {
        // If the user tries to close the tab while files are uploading, warn them
        if (isUploading) {
            window.onbeforeunload = () => {
                return true;
            };

            return () => {
                window.onbeforeunload = null
            };
        }
    }, [isUploading]);

    useEffect(() => {
        if (prevPath !== window.location.pathname && window.location.pathname !== "/") {
            setPrevPath(window.location.pathname);
        }
    }, [location.pathname, prevPath]);

    useEffect(() => {
        try {
            // Init userback.io (for user feedback)
            const userback = (window as any).Userback;
            if (!!userback && !!userEmail) {
                userback.init(Config.userbackKey, {
                    email: userEmail,
                    name: user?.name,
                    on_open: () => {
                        userback.userbackId = uuidv4();
                        userback.setData({ ...TelemetryContext.context, userbackId: userback.userbackId });

                        Telemetry.trackEvent<UserbackOpenedSchema>({
                            userbackId: userback.userbackId,
                            eventName: TelemetryEvent.UserbackOpened
                        });
                        KeyboardAccelerators.addOrRemoveDisabler(true, "Userback");
                    },
                    on_close: () => {
                        Telemetry.trackEvent<UserbackClosedSchema>({
                            userbackId: userback.userbackId,
                            eventName: TelemetryEvent.UserbackClosed
                        });
                        KeyboardAccelerators.addOrRemoveDisabler(false, "Userback");
                    },
                    before_send: () => {
                        userback.custom_data = TelemetryContext.context;

                        Telemetry.trackEvent<UserbackSendingSchema>({
                            userbackId: userback.userbackId,
                            eventName: TelemetryEvent.UserbackSending
                        });
                    },
                    after_send: () => {
                        Telemetry.trackEvent<UserbackSentSchema>({
                            userbackId: userback.userbackId,
                            eventName: TelemetryEvent.UserbackSent
                        });
                        KeyboardAccelerators.addOrRemoveDisabler(false, "Userback");
                    }
                });
            }
        }
        catch (error) {
            console.error("Userback initialization failed:", error);
        }
    }, [userEmail, user?.name]);

    useEffect(() => {
        // Init chameleon.io (for tours and tooltips)
        if (!!userEmail && !!Config.chameleonKey) {
            chmln.init(Config.chameleonKey, { fastUrl: 'https://fast.chameleon.io/', forceOverride: true });
            chmln.identify(userEmail, { email: userEmail, name: user?.name })

            const chameleonTelemetryProvider: ITelemetryProvider = new ChameleonTelemetryProvider();
            Telemetry.register(chameleonTelemetryProvider)
        }
    }, [userEmail, user?.name]);

    function navToHome(): void {
        dispatch(reportErrorEncountered('', null));
        if (loginUrl) {
            console.log(`redirecting to login page ${loginUrl}`);
            window.location.href = loginUrl;
        }
    }

    // Telemetry: initialize for web app
    useEffect(() => {
        if (!isLoading && isAuthenticated && !!userEmail && !!curOrg?.id) {
            Telemetry.setUserContext(userEmail, curOrg.id);
        }
    }, [userEmail, isLoading, isAuthenticated, curOrg])

    const isSignalRReady = useMemo(() => !!curOrg?.id && isAuthenticated, [curOrg?.id, isAuthenticated]);
    useEffect(() => {
        if (isSignalRReady) {
            dispatch(connectToOrganizationSignalR());
        }

        return () => stopSignalRConnection();
    }, [isSignalRReady, dispatch])

    useEffect(() => {
        if (!!curOrg?.id) {
            StorageManagerInstance.setOrgId(curOrg.id);
        }
    }, [curOrg])

    useEffect(() => {
        if (!!curOrg?.id) {
            dispatch(fetchOrganizationAccount());
        }
    }, [curOrg, dispatch])

    useEffect(() => {
        if ((hasOrgClaims === false || needToCreateAnOrg || currentOrgInitializing) && window.location.pathname !== '/organization') {
            navService.navToOrganizationPage();
        }
    }, [hasOrgClaims, navService, currentOrgInitializing, needToCreateAnOrg])

    return (
        <div>
            <DndProvider backend={HTML5Backend}>
                <AppShell>
                    <Suspense fallback={<PageLoadingFallback />}>
                    <Routes>
                        <Route path='/home' element={<SecuredDashboardPage />} />
                        <Route path='/dg-prod-app' element={<SecuredDashboardPage />} />
                        <Route path='/dg-staging-app' element={<SecuredDashboardPage />} />
                        <Route path='/login' element={<SecuredDashboardPage />} />

                        <Route path='/' element={<SecuredDashboardPage />} />

                        <Route path='/projects/*' element={<SecuredProjectPage />} />
                        <Route path='/understand/*' element={<SecuredProjectPage />} />
                        <Route path='/create/*' element={<SecuredProjectPage />} />
                        <Route path='/workflow/*' element={<SecuredProjectPage />} />

                        <Route path='/dashboard/*' element={<SecuredDashboardPage />} />
                        <Route path='/organization' element={<SecuredOrganization />} />

                        <Route path='/workPath/*' element={<SecuredWorkPathRouting />} />

                        <Route path='/account' element={<SecuredAccountPage />} />
                        <Route path='/tiers' element={<SecuredBillingPage />} />
                        <Route path={`/${DEVELOPER_HUB_PAGE}/:target`} element={<SecuredDeveloperHubPage />} />
                        <Route path={`/${DEVELOPER_HUB_PAGE}`} element={<SecuredDeveloperHubPage />} />

                        <Route path='*' element={<NotFoundPage />} />
                    </Routes>
                    </Suspense>
                </AppShell>
            </DndProvider>
            <ConfirmationDialog
                hidden={!errorEncountered}
                title="Oops! Something went wrong!"
                subText="We will look into it right away. Please check back again later."
                confirmButtonText="OK"
                dismissButtonText=""
                confirmedCallback={navToHome}
                dismissedCallback={navToHome} />
            <Suspense fallback={null}>
                <GlobalWorkPathModal />
                <GlobalAddDocsModal />
                <UpgradeAccountModal />
            </Suspense>
        </div>
    );
}

export default App;
